/**
 * Storage Yards Tab Component
 *
 * Main view for storage yard management.
 * Displays yard cards, summary stats, and management actions.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StorageYardCard } from './StorageYardCard';
import { CreateYardModal } from './CreateYardModal';
import { EditYardModal } from './EditYardModal';
import { useStorageYards, useDeleteStorageYard } from '@/hooks/useStorageYards';
import type { StorageYard } from '@/services/storage-yard.service';

export function StorageYardsTab() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedYard, setSelectedYard] = useState<StorageYard | null>(null);

  const { data: yardData, isLoading } = useStorageYards();
  const deleteYard = useDeleteStorageYard();

  const yards = yardData?.yards || [];
  const summary = yardData?.summary;

  const handleEdit = (yard: StorageYard) => {
    setSelectedYard(yard);
    setEditModalOpen(true);
  };

  const handleDelete = async (yard: StorageYard) => {
    if (window.confirm(`Are you sure you want to delete "${yard.name}"? This action cannot be undone.`)) {
      try {
        await deleteYard.mutateAsync(yard.id);
      } catch (error) {
        // Error toast handled by mutation
      }
    }
  };

  const handleViewContainers = (yard: StorageYard) => {
    // TODO: Navigate to containers tab with yard filter
    console.log('View containers for yard:', yard);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-sm text-gray-600">Loading storage yards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Yards</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{summary.totalYards}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Active Yards</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{summary.activeYards}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Total Containers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{summary.totalContainers}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Default Yard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-semibold text-gray-900 truncate">
                {summary.defaultYard?.name || 'None'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Storage Yards</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your storage yard locations and container inventory
          </p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          size="lg"
          className="min-h-[44px] whitespace-nowrap"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Yard
        </Button>
      </div>

      {/* Yards Grid */}
      {yards.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12">
          <div className="text-center">
            <svg
              className="mx-auto h-16 w-16 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No storage yards yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Get started by creating your first storage yard location.
            </p>
            <Button
              onClick={() => setCreateModalOpen(true)}
              className="mt-6"
              size="lg"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create First Yard
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {yards.map((yard) => (
            <StorageYardCard
              key={yard.id}
              yard={yard}
              containerCount={0} // TODO: Get actual count from API
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewContainers={handleViewContainers}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateYardModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      <EditYardModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedYard(null);
        }}
        yard={selectedYard}
      />
    </div>
  );
}
